import { Typography } from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { StepAccordion } from "src/components/ui/StepAccordion";
import { StepAccordionGroup } from "src/components/ui/StepAccordionGroup";
import { useGetUser } from "src/hooks/queries";
import { useGetQualityEventFiles } from "src/hooks/queries/quality-events";
import { OrgScopedBaseUser } from "src/stores/models/user";
import {
  QualityEventDefinition,
  QualityEventDraft,
  QualityEventMissingRequirement,
  QualityEventProposal,
  QualityEventRecord,
  QualityEventStep,
  isQualityEventApproval,
} from "src/types/quality-events";
import { getIncompleteQualityEventFieldPaths } from "./quality-event-conditions";
import {
  canEditQualityEventRecord,
  getQualityEventDraft,
  isQualityEventStepUnlocked,
} from "./quality-event-draft";
import { QualityEventDraftForm } from "./QualityEventDraftForm";

export const QualityEventReviewForm = ({
  orgId,
  proposal,
  definition,
  record,
  step,
  users,
  pending,
  error,
  actionIndex,
  renderReview,
  missingRequirements = [],
}: {
  orgId: string;
  proposal: QualityEventProposal;
  definition: QualityEventDefinition;
  record?: QualityEventRecord;
  step: QualityEventStep;
  users: OrgScopedBaseUser[];
  pending: boolean;
  error: string | null;
  actionIndex: number;
  renderReview: (review: {
    fields: ReactNode;
    drafts: Record<string, QualityEventDraft>;
  }) => ReactNode;
  missingRequirements?: QualityEventMissingRequirement[];
}) => {
  const files = useGetQualityEventFiles({ orgId });
  const { data: user } = useGetUser();
  const editable = !record || canEditQualityEventRecord(record, user);
  const steps = record
    ? record.definition.definition.steps.filter((step) =>
        isQualityEventStepUnlocked(record, step.key)
      )
    : [step];
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>(
    {}
  );
  const [drafts, setDrafts] = useState<Record<string, QualityEventDraft>>(() =>
    Object.fromEntries(
      steps.map((step) => {
        const stepProposal =
          step.key === proposal.stepKey
            ? proposal
            : {
                qualityEventKey: proposal.qualityEventKey,
                stepKey: step.key,
                expectedVersion: proposal.expectedVersion,
                title: proposal.title,
                answers: {},
                ...proposal.steps?.[step.key],
              };
        return [
          step.key,
          getQualityEventDraft({
            step,
            record,
            users,
            assignment: definition.definition.assignment,
            creatorId: user?.id,
            proposal: editable ? stepProposal : undefined,
          }),
        ];
      })
    )
  );
  const editableDrafts = editable ? drafts : {};
  const workflowAnswers = Object.assign(
    {},
    ...Object.values(record?.steps ?? {}).map((step) => step.answers),
    ...Object.values(editableDrafts).map((draft) => draft.answers)
  );
  const invalidFieldPathsByStep: Record<
    string,
    Set<string>
  > = Object.fromEntries(
    steps.map((currentStep) => {
      const requirementPaths = new Set(
        missingRequirements
          .find((group) => group.stepKey === currentStep.key)
          ?.requirements.flatMap((requirement) => requirement.fieldPaths) ?? []
      );
      return [
        currentStep.key,
        getIncompleteQualityEventFieldPaths({
          fields: currentStep.fields,
          values: drafts[currentStep.key].answers,
          allAnswers: workflowAnswers,
          completionRequirements: currentStep.completionRequirements,
          files: drafts[currentStep.key].files,
          requiredFieldPaths: requirementPaths,
          validateAllRequirements: missingRequirements.length > 0,
        }),
      ];
    })
  );
  const forms = Object.fromEntries(
    steps.map((currentStep) => {
      const draft = drafts[currentStep.key];
      return [
        currentStep.key,
        <Fragment key={currentStep.key}>
          {!editable && (
            <Typography variant="body2" color="text.secondary" className="mb-3">
              Only a responsible person, an action owner, the record owner, or
              an admin can edit this record.
            </Typography>
          )}
          <QualityEventDraftForm
            orgId={orgId}
            step={currentStep}
            record={record}
            workflowAnswers={workflowAnswers}
            draft={draft}
            formId={`review-${draft.id}-${currentStep.key}-${actionIndex}`}
            userId={user?.id ?? ""}
            currentUser={user}
            existingFiles={files.data ?? []}
            users={users}
            onDraftChange={
              editable
                ? (nextDraft) => {
                    setDrafts((previous) => ({
                      ...previous,
                      [currentStep.key]: nextDraft,
                    }));
                  }
                : undefined
            }
            pending={pending}
            error={error}
            hasUnsavedChanges
            invalidRequirementPaths={Array.from(
              invalidFieldPathsByStep[currentStep.key]
            )}
          />
        </Fragment>,
      ];
    })
  );
  const reviewSteps = steps.map((currentStep) => {
    const stepNumber =
      definition.definition.steps.findIndex(
        (candidate) => candidate.key === currentStep.key
      ) + 1;
    const savedStep = record?.steps[currentStep.key];
    const status = savedStep?.completedAt ? "complete" : "current";
    const missingRequirementCount = missingRequirements.some(
      (group) => group.stepKey === currentStep.key
    )
      ? invalidFieldPathsByStep[currentStep.key].size
      : 0;
    const waitingForApproval = Object.values(savedStep?.answers ?? {}).some(
      (answer) => isQualityEventApproval(answer) && answer.status === "PENDING"
    );
    return {
      step: currentStep,
      stepNumber,
      status,
      missingRequirementCount,
      waitingForApproval,
      expanded:
        expandedSteps[currentStep.key] ?? currentStep.key === proposal.stepKey,
      content: forms[currentStep.key],
    } as const;
  });
  const fields = (
    <>
      {!record ? (
        forms[step.key]
      ) : (
        <StepAccordionGroup>
          {reviewSteps.map(
            ({
              step,
              stepNumber,
              status,
              missingRequirementCount,
              waitingForApproval,
              expanded,
              content,
            }) => (
              <StepAccordion
                key={step.key}
                stepNumber={stepNumber}
                title={step.title}
                status={status}
                missingRequirementCount={missingRequirementCount}
                waitingForApproval={waitingForApproval}
                disabled={pending}
                expanded={expanded}
                onExpandedChange={(nextExpanded) =>
                  setExpandedSteps((previous) => ({
                    ...previous,
                    [step.key]: nextExpanded,
                  }))
                }
                detailsLabel={`${step.title} review`}
              >
                {content}
              </StepAccordion>
            )
          )}
        </StepAccordionGroup>
      )}
    </>
  );
  return renderReview({ fields, drafts: editableDrafts });
};
